/**
 * INSTALL PWA COMPONENT
 *
 * Shows an install prompt for users to add the app to their home screen.
 * Only appears on supported browsers when the app meets PWA criteria.
 *
 * CREATED: July 2025
 */

import React, { useState, useEffect } from 'react';
import './InstallPWA.scss';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode) - multiple methods for reliability
    const checkStandalone = () => {
      // Method 1: Display mode media query (works on most modern browsers)
      const displayMode = window.matchMedia('(display-mode: standalone)').matches;

      // Method 2: iOS Safari standalone property
      const iosStandalone = window.navigator.standalone === true;

      // Method 3: Android app referrer
      const androidApp = document.referrer.includes('android-app://');

      // Method 4: Check URL parameters (some browsers add these when launched from home screen)
      const urlParams = new URLSearchParams(window.location.search);
      const launchedFromHome = urlParams.get('utm_source') === 'homescreen' ||
                               urlParams.get('standalone') === 'true';

      // Method 5: Check localStorage for previous installation
      const previouslyInstalled = localStorage.getItem('pwa-installed') === 'true';

      // Method 6: Check if running in fullscreen mode (another indicator of PWA)
      const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;

      // Method 7: Check window dimensions match screen dimensions (PWA indicator)
      const isFullWindow = window.innerWidth === window.screen.width && window.innerHeight >= window.screen.height - 100;

      return displayMode || iosStandalone || androidApp || launchedFromHome ||
             previouslyInstalled || isFullscreen || (isFullWindow && !window.menubar?.visible);
    };

    const standalone = checkStandalone();
    setIsStandalone(standalone);

    // If standalone, also persist to localStorage for future checks
    if (standalone) {
      localStorage.setItem('pwa-installed', 'true');
    }

    // Check if iOS - multiple detection methods for reliability
    const ua = window.navigator.userAgent;
    const uaLower = ua.toLowerCase();

    // Method 1: Check user agent for iPhone/iPad/iPod
    const uaCheck = /iphone|ipad|ipod/i.test(ua);

    // Method 2: Check for iOS-specific properties
    const touchCheck = navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua);

    // Method 3: Check platform (works on older iOS)
    const platformCheck = /iPhone|iPad|iPod/.test(navigator.platform);

    // Method 4: Check for webkit on mobile without Chrome/Android indicators
    const webkitMobile = /AppleWebKit/i.test(ua) && /Mobile/i.test(ua) && !/Android/i.test(ua);

    const ios = uaCheck || touchCheck || platformCheck || webkitMobile;
    setIsIOS(ios);

    // Check if Safari (PWA install only works in Safari on iOS)
    const safari = ios && /safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    setIsSafari(safari);

    // Check if previously dismissed - use 3 days for quicker re-prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    const canShowPrompt = !standalone && daysSinceDismissed > 3;

    // For iOS, show install instructions after delay (no beforeinstallprompt event)
    if (ios && canShowPrompt) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    // Listen for the beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Only show prompt if not dismissed recently (within 3 days)
      if (canShowPrompt) {
        // Delay showing the prompt for better UX
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
      // Persist installation state to localStorage
      localStorage.setItem('pwa-installed', 'true');
      // Also remove any dismissal timestamp so it doesn't conflict
      localStorage.removeItem('pwa-install-dismissed');
      console.log('PWA installed successfully');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    // Listen for display mode changes (some browsers update this after installation)
    const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      if (e.matches) {
        setIsStandalone(true);
        setShowPrompt(false);
        localStorage.setItem('pwa-installed', 'true');
      }
    };
    standaloneMediaQuery.addEventListener('change', handleDisplayModeChange);

    // Re-check on visibility change (when user comes back to the app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const nowStandalone = checkStandalone();
        if (nowStandalone) {
          setIsStandalone(true);
          setShowPrompt(false);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      standaloneMediaQuery.removeEventListener('change', handleDisplayModeChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      // Mark as installed in localStorage (appinstalled event will also do this, but this is a backup)
      localStorage.setItem('pwa-installed', 'true');
      localStorage.removeItem('pwa-install-dismissed');
      setIsStandalone(true);
    } else {
      console.log('User dismissed the install prompt');
      // Mark as dismissed so we don't show again for 3 days
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't render if already installed or prompt shouldn't show
  if (isStandalone || !showPrompt) return null;

  // iOS - always show manual instructions (PWA install only works via Safari share menu)
  if (isIOS) {
    return (
      <div className="install-pwa install-pwa--ios">
        <button className="install-pwa__close" onClick={handleDismiss}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="install-pwa__content">
          <div className="install-pwa__icon">
            <img src="/logo-iD.png" alt="iDyaNova" />
          </div>
          <div className="install-pwa__text">
            <p className="install-pwa__title">Install iDyaNova</p>
            <p className="install-pwa__subtitle">
              {isSafari ? (
                <>
                  Tap Safari's <span className="install-pwa__share-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </span> at screen bottom, then "Add to Home Screen"
                </>
              ) : (
                <>Open in Safari to install</>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop - show install button if browser supports it
  // If no deferredPrompt, the button won't do anything useful
  return (
    <div className="install-pwa">
      <button className="install-pwa__close" onClick={handleDismiss}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="install-pwa__content">
        <div className="install-pwa__icon">
          <img src="/logo-iD.png" alt="iDyaNova" />
        </div>
        <div className="install-pwa__text">
          <p className="install-pwa__title">Install iDyaNova App</p>
          <p className="install-pwa__subtitle">Get quick access from your home screen</p>
        </div>
        {deferredPrompt ? (
          <button className="install-pwa__btn" onClick={handleInstallClick}>
            Install
          </button>
        ) : (
          <p className="install-pwa__subtitle">Use browser menu to install</p>
        )}
      </div>
    </div>
  );
};

export default InstallPWA;
